
    /* global server */
    import { defineComponent } from 'vue';
    import { useResultsStore } from '../../stores/citation-search-results';
    import { useUserStore } from './../../stores/user';
    import { useCompletedPaymentStore } from './../../stores/completed-payment';
    import { useProgrammatic } from '@oruga-ui/oruga-next'
    import { Form, Field } from "vee-validate";
    import * as yup from "yup";
    import VuePaycard from "vue-paycard";
    import states from 'states-us';
    import { AxiosResponse } from 'axios';


    export default defineComponent({
        components: {
            Form,
            Field,
            VuePaycard,
        },
        directives: {
            'number-only': {
                beforeMount(el) {
                    function checkValue(event: any) {
                        event.target.value = event.target.value.replace(/[^0-9]/g, '')
                        if (event.charCode >= 48 && event.charCode <= 57) {
                            return true
                        }
                        event.preventDefault()
                    }
                    el.addEventListener('keypress', checkValue)
                }
            },
            'letter-only': {
                beforeMount(el) {
                    function checkValue(event: any) {
                        if (event.charCode >= 48 && event.charCode <= 57) {
                            event.preventDefault()
                        }
                        return true
                    }
                    el.addEventListener('keypress', checkValue)
                }
            }
        },
        data() {

            const { oruga } = useProgrammatic();

            function generateNotification(message: string, variant: string) {
                oruga.notification.open({
                    message: message,
                    rootClass: 'toast-notification',
                    variant: variant,
                    position: 'top'
                })
            }

            return {
                states,
                isSaving: false,
                generateNotification,
                results: useResultsStore(),
                userStore: useUserStore(),
                paymentCompleteStore: useCompletedPaymentStore(),
                paymentType: "ACH",
                minCardYear: new Date().getFullYear(),
                creditCardType: null as null | string,
                cardNumberMaxLength: 19,
                creditCardValues: {
                    cardName: "",
                    cardNumber: "",
                    cardMonth: "",
                    cardYear: "",
                    cardCvv: "",
                },
                form: {
                    FirstName: "",
                    LastName: "",
                    StreetAddress: "",
                    City: "",
                    State: "",
                    Zip: "",
                    PhoneNumber: "",
                    EmailAddress: useUserStore().user?.EmailAddress || null,
                    /** Case Info */
                    InquiryID: useResultsStore().Overview.InquiryID,
                    /** Bank Info */
                    BankRoutingNumber: "",
                    BankAccountNumber: "",
                    /** Credit Card Info */
                    CreditCardNumber: "",
                    CreditCardHolder: "",
                    CreditCardExpMonth: 0,
                    CreditCardExpYear: 0,
                    CreditCardCVV: "",
                    /** How much the user will pay total. */
                    AmountToPay: useResultsStore().Overview.AmountToPay,
                } as server.PaymentInfo,
                accountConfirmation: "",
                masks: {
                    dateMask: '##/##/####',
                    phoneMask: '(###) ###-####',
                    zipMask: '#####',
                    ccMask: '###################',
                    ccvMask: '####',
                }
            }
        },
        computed: {

            schema(): any {
                var self = this;
                return yup.object().shape({
                    form: yup.object({
                        FirstName: yup.string().required('First Name is required'),
                        LastName: yup.string().required('Last Name is required'),
                        StreetAddress: yup.string().required('Street Address is required'),
                        City: yup.string().required('City is required'),
                        State: yup.string().required('State is required'),
                        Zip: yup.string().required('Zip is required'),
                        PhoneNumber: yup.string().nullable().required('Phone Number is required'),
                        EmailAddress: yup.string().nullable().required('Email Address is required').email('Must be a valid email address'),
                        BankRoutingNumber: yup.string().min(9, 'Must be at least 9 numbers').max(9, 'Must be 9 numbers').test('BankRoutingNumber-required', 'Bank routing number is required', (value, context: any) => {
                            return context.from[1].value.paymentType != "ACH" || (context.from[1].value.paymentType == "ACH" && !!value)
                        }).test('ValidBankRoutingNumber', 'Must be a valid US routing number', (value) => {
                            // Run through each digit and calculate the total.
                            if (!value) {
                                return true;
                            }

                            let n = 0;
                            for (let i = 0; i < (value as string).length; i += 3) {
                                n += parseInt((value as string).charAt(i), 10) * 3
                                    + parseInt((value as string).charAt(i + 1), 10) * 7
                                    + parseInt((value as string).charAt(i + 2), 10);
                            }

                            // If the resulting sum is an even multiple of ten (but not zero),
                            // the aba routing number is good.
                            if (n != 0 && n % 10 == 0) {
                                return true;
                            } else {
                                return false;
                            }
                        }),
                        BankAccountNumber: yup.string().test('BankAccountNumber-required', 'Bank account number is required', (value, context: any) => {
                            return context.from[1].value.paymentType != "ACH" || (context.from[1].value.paymentType == "ACH" && !!value)
                        }),
                        CreditCardNumber: yup.string().test('CreditCardNumber-required', 'Credit card number is required', (value, context: any) => {
                            return context.from[1].value.paymentType != "CC" || (context.from[1].value.paymentType == "CC" && !!value)
                        }).test('CreditCardNumber-type', 'Credit card must be a Visa, Mastercard or Discover card', (value, context: any) => {
                            return context.from[1].value.paymentType != "CC" || (context.from[1].value.paymentType == "CC" && !!value && ((self as any).creditCardType === 'visa' || (self as any).creditCardType == 'mastercard' || (self as any).creditCardType == 'discover'))
                        }),
                        CreditCardHolder: yup.string().test('CreditCardHolder-required', 'Credit card holder is required', (value, context: any) => {
                            return context.from[1].value.paymentType != "CC" || (context.from[1].value.paymentType == "CC" && !!value)
                        }),
                        CreditCardExpMonth: yup.string().test('CreditCardExpMonth-required', 'Exp month is required', (value, context: any) => {
                            return context.from[1].value.paymentType != "CC" || (context.from[1].value.paymentType == "CC" && !!value && value != "0")
                        }),
                        CreditCardExpYear: yup.string().test('CreditCardExpYear-required', 'Exp year is required', (value, context: any) => {
                            return context.from[1].value.paymentType != "CC" || (context.from[1].value.paymentType == "CC" && !!value && value != "0")
                        }),
                        CreditCardCVV: yup.string().test('CreditCardCVV-required', 'CVV is required', (value, context: any) => {
                            return context.from[1].value.paymentType != "CC" || (context.from[1].value.paymentType == "CC" && !!value)
                        }),
                    }),
                    paymentType: yup.string().required(),
                    accountConfirmation: yup.string().test('account-match', 'Bank account numbers must match', function (value) {
                        return this.parent.form.BankAccountNumber === value
                    }).test('accountConfirmation-required', 'Bank account number confirmation is required', (value, context: any) => {
                        return context.from[0].value.paymentType != "ACH" || (context.from[0].value.paymentType == "ACH" && !!value)
                    }),
                })
            },

            minCardMonth(): number {
                // @ts-ignore
                if (this.form.CreditCardExpYear === this.minCardYear) { return new Date().getMonth() + 1 }
                return 1
            },
            minPayment(): number {
                return (this.results.Overview.PartialFeeFineContemptPaymentAmountDue || 0) + (this.results.Overview.PartialProbationPaymentAmountDue || 0);
            },
            convenienceFee(): number {
                return ((this.results.Overview.AmountToPay || 0) * ((this.results.Overview.ConvenienceFee || 0) / 100));
            },
            totalAmount(): number {
                return (this.results.Overview.AmountToPay || 0) + (this.paymentType === "CC" ? this.convenienceFee : 0);
            },
            isLoggedIn(): boolean {
                return this.userStore.user != null && Object.keys(this.userStore.user).length !== 0;
            }
        },
        methods: {
            changeName(e: any) {
                this.form.CreditCardHolder = e.target.value;
                this.creditCardValues.cardName = this.form.CreditCardHolder;
                this.$emit('input-card-name', this.form.CreditCardHolder);
            },
            changeNumber(e: any) {
                this.form.CreditCardNumber = e.target.value;
                const value = this.form.CreditCardNumber.replace(/\D/g, '');
                // american express, 15 digits
                if (/^3[47]\d{0,13}$/.test(value)) {
                    this.form.CreditCardNumber = value
                        .replace(/(\d{4})/, '$1 ')
                        .replace(/(\d{4}) (\d{6})/, '$1 $2 ');
                    this.cardNumberMaxLength = 17;
                } else if (/^3(?:0[0-5]|[68]\d)\d{0,11}$/.test(value)) {
                    // diner's club, 14 digits
                    this.form.CreditCardNumber = value
                        .replace(/(\d{4})/, '$1 ')
                        .replace(/(\d{4}) (\d{6})/, '$1 $2 ');
                    this.cardNumberMaxLength = 16;
                } else if (/^62[0-9]\d*/.test(value)) {
                    this.form.CreditCardNumber = value
                        .replace(/(\d{6})/, '$1 ')
                        .replace(/(\d{6}) (\d{7})/, '$1 $2 ')
                        .replace(/(\d{6}) (\d{7}) (\d{6})/, '$1 $2 $3 ')
                        .replace(/(\d{5}) (\d{5}) (\d{5}) (\d{4})/, '$1 $2 $3 $4');
                    this.cardNumberMaxLength = 21
                } else if (/^\d{0,16}$/.test(value)) {
                    // regular cc number, 16 digits
                    this.form.CreditCardNumber = value
                        .replace(/(\d{4})/, '$1 ')
                        .replace(/(\d{4}) (\d{4})/, '$1 $2 ')
                        .replace(/(\d{4}) (\d{4}) (\d{4})/, '$1 $2 $3 ');
                    this.cardNumberMaxLength = 19;
                }
                // eslint-disable-next-line
                if (e.inputType == "deleteContentBackward") {
                    const lastChar = this.form.CreditCardNumber.substring(
                        this.form.CreditCardNumber.length,
                        this.form.CreditCardNumber.length - 1
                    );
                    // eslint-disable-next-line
                    if (lastChar == " ") {
                        this.form.CreditCardNumber = this.form.CreditCardNumber.substring(
                            0,
                            this.form.CreditCardNumber.length - 1
                        );
                    }
                }

                this.creditCardValues.cardNumber = this.form.CreditCardNumber;
                this.$emit('input-card-number', this.form.CreditCardNumber);
            },
            changeCCMonth() {
                //@ts-ignore
                this.creditCardValues.cardMonth = this.form.CreditCardExpMonth;
            },
            changeCCYear() {
                //@ts-ignore
                this.creditCardValues.cardYear = this.form.CreditCardExpYear;
            },
            changeCvv(e: any) {
                this.form.CreditCardCVV = e.target.value;
                //@ts-ignore
                this.creditCardValues.cardCvv = this.form.CreditCardCVV;
                this.$emit('input-card-cvv', this.form.CreditCardCVV);
            },
            getCreditCardType(type: string) {
                this.creditCardType = type;
            },
            changePaymentType() {
                if (this.paymentType == "CC") {
                    this.form.BankRoutingNumber = "";
                    this.form.BankAccountNumber = "";
                    this.accountConfirmation = "";

                } else if (this.paymentType == "ACH") {
                    this.form.CreditCardNumber = "";
                    this.form.CreditCardHolder = "";
                    this.form.CreditCardExpMonth = 0;
                    this.form.CreditCardExpYear = 0;
                    this.form.CreditCardCVV = "";
                }
            },
            generateMonthValue(n: any) {
                return n < 10 ? `0${n}` : n
            },
            isNumeric(num: string) {
                return !isNaN(num as any)
            },
            processPayment() {
                this.isSaving = true;
                this.$axios.post('Payment/ProcessPayment', this.form).then((response: AxiosResponse<string>) => {
                    if ((response.status == 200 || response.status == 204) && this.isNumeric(response.data) && response.data !== null && response.data !== "") {
                        this.paymentCompleteStore.setPaymentID(+response.data, (this.results.Attachments !== null && this.results.Attachments.length > 0), this.results.Overview.AttachmentVerbiage);
                        this.results.clearResults();                     
                        this.$router.push({ path: '/payment-confirmation' })
                    } else {
                        this.isSaving = false;
                        this.generateNotification(response.data, 'danger');
                    }

                })
            }
        },

    });

